import { useFieldArray, useForm, useWatch } from "react-hook-form";
import {
  DatePicker,
  Input,
  Label,
  MultipleSelect,
  Select,
  Submit,
  TextArea,
} from "../FormComponents";
import useLabels from "../../hooks/useLabels";

// NEW: Import CacheManager for surgical cache updates
import CacheManager from "../../services/cacheManager";
import { useNavigate, useParams } from "react-router";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { batch, useDispatch, useSelector } from "react-redux";
import {
  getDrugs,
  getOperations,
  getPomacesList,
  getProtocols,
  getUnits,
  getWineLabels,
  newOperation,
  updateOperation,
} from "../../services/utils";
import {
  buildPomace,
  ConfirmationModal,
  CTA,
  DestinationSelector,
  DrugsBatchSelectorTable,
  OperationRecap,
  PomacesDestination,
  SourceSelector,
  Tooltip,
} from "../Generic";
import {
  ALPHA_NUM_SPACE_DASH_UNDERSCORE_REGEX,
  INT_FLOAT_REGEX,
  INT_REGEX,
} from "../../constants/regex";
import {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { toast } from "react-toastify";
import {
  selectAllLotNames,
  selectDrugs,
  selectOperations,
  selectTreatments,
  selectUnits,
  selectVirtualTanks,
  selectWineLabels,
} from "../../context/selectors";
import { setWineLabels } from "../../context/wineLabels/wineLabelsSlice";
import {
  add,
  empty,
  setOperations,
  updateOp,
} from "../../context/operations/operationsSlice";
import { emptyTanks } from "../../context/tanks/tanksSlice";
import { emptyLots } from "../../context/lots/lotsSlice";
import {
  assignDrugBatches,
  buildTreatmentsDataArray,
  computeRequiredNumericQty,
  computeRequiredWarehouseQty,
  getWarehouseUnit,
  groupBySubstanceId,
  initializeDrugBatches,
  initializeProtocolId,
} from "../../services/common";
import TableEdit from "../TableEdit";
import { createColumnHelper } from "@tanstack/react-table";
import TableCell from "../TableEdit/TableCell";
import { setDrugs } from "../../context/substances/drugsSlice";
import { setTreatments } from "../../context/protocols/treatmentsSlice";
import { setUnits } from "../../context/units/unitsSlice";
import EditCell from "../TableEdit/EditCell";
import { toastAxios } from "../../services/toastAxios";

const DestemmingForm = forwardRef(
  (
    {
      type,
      operation,
      lots,
      activeWinery,
      tanks,
      users,
      configs,
      isMobile,
      isDuplicate = false,
      onConfigSave = null,
      lockedSubtype = null,
      sourceBatchId = null,
    },
    formRef,
  ) => {
    const { id } = useParams();
    const [getLabel] = useLabels();
    const navigate = useNavigate();
    const axiosPrivate = useAxiosPrivate();
    const dispatch = useDispatch();
    const wineLabels = useSelector(selectWineLabels);
    const [isOpen, setIsOpen] = useState(false);
    const [isExactDate, setIsExactDate] = useState(false);
    const [pomacesRecipients, setPomacesRecipients] = useState([]);
    // const [newBatchNames, setNewBatchNames] = useState(new Set());
    const operations = useSelector(selectOperations);
    const protocols = useSelector(selectTreatments);
    const drugs = useSelector(selectDrugs);
    const units = useSelector(selectUnits);
    const lotNames = useSelector(selectAllLotNames);
    // const [isPressed, setIsPressed] = useState([]);
    const [lotTypeMap, setLotTypeMap] = useState({});
    const [isBatchSelectorModal, setIsBatchSelectorModal] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isModifyMode, setIsModifyMode] = useState(-1);
    const [operationsP, setOperationsP] = useState([]);
    const [isEnoughStock, setIsEnoughStock] = useState(false);

    const isWizardMode =
      !id && operation && onConfigSave && operation?.dest_tanks?.length > 0;

    // Track which operation ID was last reset to prevent infinite loops
    const lastResetOperationRef = useRef(null);

    // const virtualTanks = useSelector(selectVirtualTanks);

    const {
      watch,
      register,
      handleSubmit,
      formState: { errors },
      setError,
      clearErrors,
      reset,
      setValue,
      getValues,
      resetField,
      control,
      trigger,
    } = useForm({
      defaultValues: {
        column: {},
        src_tanks: operations?.src_tanks,
        dest_tanks: operation?.dest_tanks || [
          {
            tankData: { value: null, label: null },
            quantity: 0,
            new_batch_name: "",
            batch_name_selector: { value: false, label: "" },
          },
        ],
        // batchType: operation?.batchType || "",
        expire_date: operation?.expire_date || null,
        type: "DESTEMMING",
        subtype: {
          value: "WITH_PRESSING",
          label: getLabel("operationSubtypeWITH_PRESSING"),
        },
        batch_id_in: operation?.batch_id_in || "",
        priority: operation?.priority || "",
        cellarman_ids: operation?.cellarman_ids || "",
        new_batch_name: operation?.new_batch_name || "",
        note: operation?.note || "",
        pomaces: { quantity: 0, cost: 0, close_notes: "" },
        "pomaces.dest": { value: null, label: "", is_field: false },
        "pomaces.reason": {
          value: "STEM",
          label: getLabel("pomaceReason_STEM"),
        },
        pomacesW: { quantity: 0, cost: 0, close_notes: "" },
        "pomacesW.dest": { value: null, label: "", is_field: false },
        "pomacesW.reason": {
          value: "SKIN",
          label: getLabel("pomaceReason_SKIN"),
        },
      },
      mode: "onTouched",
    });

    const {
      register: modalRegister,
      reset: modalReset,
      handleSubmit: modalHandleSubmit,
      formState: { errors: modalErrors },
      setValue: modalSetValue,
      getValues: modalGetValues,
      setError: modalSetError,
      clearErrors: modalClearErrors,
      control: modalControl,
    } = useForm({
      defaultValues: {
        drug_batches_tmp: [{ value: null, label: null }],
        // day: protocol?.ay || "",
        // phase: protocol?.phase || "",
        // operation: protocol?.operation || "",
      },
      mode: "onTouched",
    });

    const {
      batch_id_in,
      dest_tanks,
      protocol_id,
      pomaces,
      pomacesW,
      drug_batches,
      subtype,
    } = useWatch({
      control,
    });

    const [totalTreatmentVolume, setTotalTreatmentVolume] = useState(0);

    // Esponi il metodo submitForm tramite formRef per il wizard
    useImperativeHandle(
      formRef,
      () => ({
        submitForm: async () => {
          // Trigger validation for all fields (because mode: "onTouched")
          const isFormValid = await trigger();
          if (!isFormValid) {
            console.log("Form has validation errors:", errors);
            return;
          }
          handleSubmit((data) => {
            if (onConfigSave) {
              return sendData();
            } else {
              return openFormModal();
            }
          })();
        },
      }),
      [handleSubmit, onConfigSave, trigger, errors],
    );

    const drugBatchFieldArray = useFieldArray({
      control,
      name: "drug_batches",
    });

    const drugBatchesFieldArrayTmp = useFieldArray({
      control: modalControl,
      name: "drug_batches_tmp",
    });

    const setModifyMode = (row_id) => {
      setIsModifyMode(row_id);
    };

    const handleOpenModal = (isModifyId = -1, modalType) => {
      modalClearErrors();

      setIsBatchSelectorModal(modalType);
      setIsModalOpen(true);
      setModifyMode(isModifyId);

      if (batch_id_in?.color === "WHITE") {
        if (isModifyId === -1) {
          reset({
            drug_batches_tmp: [],
          });
        } else if (isModifyId > 0) {
          const drugBatches = getValues("drug_batches") || [];

          const tmp = drugs?.find((cc) => cc.id === isModifyId) || {};
          const unit = units?.find((u) => u.id === tmp?.unit_id)?.unit || "";
          const substance = {
            ...tmp,
            unit: unit,
            substance_id: tmp?.id,
            drug_batches: drugBatches?.[isModifyId] || [],
          };
          const groupedTreatmentsBySubstanceId = groupBySubstanceId([
            substance,
          ]); // trick to use operation?.treatments
          const d = groupedTreatmentsBySubstanceId?.[0] || null;

          if (d && (d?.drug_batches || [])?.length > 0) {
            setValue("drug_batches_tmp", d?.drug_batches);
          }
        }
      }
    };

    useEffect(() => {
      const totalVolume =
        dest_tanks?.reduce((sum, b) => sum + (Number(b?.quantity) || 0), 0) ||
        0;
      if (totalVolume) setTotalTreatmentVolume(totalVolume);
    }, [dest_tanks]);

    const {
      fields,
      append,
      prepend,
      remove,
      swap,
      move,
      insert,
      replace,
      update,
    } = useFieldArray({
      control,
      name: "dest_tanks",
    });

    const loadOperations = async () => {
      const loadedOperations = await getOperations(axiosPrivate);
      dispatch(setOperations(loadedOperations));
    };

    const loadUnits = async () => {
      const currentUnits = await getUnits(axiosPrivate);
      dispatch(setUnits(currentUnits));
    };

    const loadDrugs = async () => {
      const currentDrugs = await getDrugs(axiosPrivate);
      dispatch(setDrugs(currentDrugs));
    };

    const loadTreatments = async () => {
      const currentTreatments = await getProtocols("treatment", axiosPrivate);
      dispatch(setTreatments(currentTreatments));
    };

    const loadWineLabels = async () => {
      const currentWineLabels = await getWineLabels(axiosPrivate);
      dispatch(setWineLabels(currentWineLabels));
    };

    useEffect(() => {
      if (type !== "DESTEMMING" || isWizardMode) return;

      const fetchPomaces = async () => {
        const pomaces = await getPomacesList(axiosPrivate);
        setPomacesRecipients(pomaces);
      };
      fetchPomaces();
    }, []);

    // Pre-fill batch_id_in from sourceBatchId (wizard mode)
    useEffect(() => {
      if (sourceBatchId && lots?.length > 0) {
        const sourceLot = lots?.find((l) => l?.id === sourceBatchId);
        if (sourceLot && !getValues("batch_id_in")?.value) {
          setValue("batch_id_in", {
            value: sourceLot?.id,
            label: sourceLot?.name,
            color: sourceLot?.color,
            initial_quantity: sourceLot?.quantity,
            quantity: sourceLot?.actual_quantity,
            planned_quantity: sourceLot?.planned_quantity,
            shared_operation_ids: sourceLot?.shared_operation_ids,
            composition: sourceLot?.composition,
            is_planned: sourceLot?.is_planned,
            arrow_quantity: 0,
          });
        }
      }
    }, [sourceBatchId, lots]);

    useEffect(() => {
      initializeProtocolId(type, protocols, operation?.protocol_id, setValue);
    }, [operation?.protocol_id, protocols]);

    useEffect(() => {
      if (!wineLabels || wineLabels.length === 0) {
        loadWineLabels();
      }
      if (!drugs || drugs.length === 0) {
        loadDrugs();
      }
      if (!protocols || protocols.length === 0) {
        loadTreatments();
      }
      if (!units || units.length === 0) {
        loadUnits();
      }

      if (!id && type === "DESTEMMING") {
        setValue("pomaces.reason", {
          label: getLabel("pomaceReason_STEM"),
          value: "STEM",
        });
        setValue("pomacesW.reason", {
          label: getLabel("pomaceReason_SKIN"),
          value: "SKIN",
        });

        const tmpLotTypeMap = {};
        for (const l of lotNames) {
          if (l?.state === "WINE") {
            tmpLotTypeMap[l?.name] = l?.batch_type; // === 0; // true = PRESSED
          }
        }

        setLotTypeMap(tmpLotTypeMap);
      }

      if (!isWizardMode && id && (!operations || operations?.length === 0))
        loadOperations();
      if (isWizardMode || (id && operations?.length > 0)) {
        const op = isWizardMode
          ? operation
          : operations?.find((o) => o.id === Number(id)) || null;
        if (op === null) return;

        if (op?.subtype)
          setValue("subtype", {
            value: op?.subtype,
            label: getLabel(`operationSubtype${op?.subtype}`),
          });
        if (op?.subtype === "CARBONIC_MACERATION")
          setValue("carbonic_output_state", {
            value: op?.metadata?.carbonic_output_state || "WINE",
            label: getLabel(
              `lotState_${op?.metadata?.carbonic_output_state || "WINE"}`,
            ),
          });

        const lot = lots?.find((l) => l.id === op?.batch_id_in);
        const isPlannedByOtherOps =
          lot?.is_planned && lot?.shared_operation_ids?.length > 0
            ? id
              ? lot.shared_operation_ids.some(
                  (opId) => Number(opId) !== Number(id),
                )
              : true
            : false;

        if (!isWizardMode)
          setValue("batch_id_in", {
            value: lot?.id,
            label: lot?.name,
            color: lot?.color,
            initial_quantity: lot?.quantity,
            quantity: lot?.actual_quantity,
            planned_quantity: lot?.planned_quantity,
            planned_pomaces: lot?.planned_pomaces,
            shared_operation_ids: lot?.shared_operation_ids,
            composition: lot?.composition,
            is_planned: isPlannedByOtherOps,
            arrow_quantity: getTotalQuantity(true),
          });

        // const chosenQuantity = operation?.dest_tanks?.reduce((acc, curr) => {
        //   return acc + (Number(curr?.quantity) || 0);
        // }, 0);
        // setValue("batch_id_in_quantity", chosenQuantity);

        setValue("priority", {
          label: getLabel(`operationPriority${op?.priority}`),
          value: op?.priority,
        });

        // setValue("batchType", {
        //   label: getLabel(`lotType${operation?.batch_type}`),
        //   value: operation?.batch_type,
        // });

        setDestTanks();

        // setNewBatchNames(computeLotNames());

        if (op?.type === "DESTEMMING") {
          const tmpPomaces = op?.pomaces?.[0] || {};
          const pomaces = {
            quantity: tmpPomaces?.quantity,
            cost: tmpPomaces?.cost,
            close_notes: tmpPomaces?.notes || "",
          };
          setValue("pomaces", pomaces);
          setValue("pomaces.dest", {
            value: isWizardMode ? 0 : tmpPomaces?.id,
            label: tmpPomaces?.recipient,
            is_field: tmpPomaces?.is_field === true,
          });

          setValue("pomaces.reason", {
            value: tmpPomaces?.waste_type,
            label: getLabel(`pomaceReason_${tmpPomaces?.waste_type}`),
          });

          if (
            lot?.color === "WHITE" ||
            (lot?.color === "RED" &&
              ["ROSE_MACERATION", "CARBONIC_MACERATION"].includes(
                subtype?.value,
              ))
          ) {
            initializeProtocolId(type, protocols, op?.protocol_id, setValue);
            initializeDrugBatches(op, drugs, setValue);
            initializeWhitePomaces(op);
          }
        }
        const cellarmans = [];
        op?.cellarman_ids?.map((u) => {
          const user = users?.find((us) => us.id === u);
          cellarmans.push({
            value: u,
            label: user?.username,
          });
        });
        setValue("cellarman_ids", cellarmans);

        setValue("expire_date", op?.expire_date);

        setValue("note", op?.note);
      }
      // // Wizard mode: reusa lo stesso setup del case edit
      // if (!id && operation && onConfigSave && operation?.dest_tanks?.length > 0) {
      //   console.log('DestemmingForm: Wizard mode setup with operation:', operation);
      //   if (operation?.subtype) setValue("subtype", { value: operation?.subtype, label: getLabel(`operationSubtype${operation?.subtype}`) });

      //   const lot = lots?.find((l) => l.id === operation?.batch_id_in);
      //   const isPlannedByOtherOps =
      //     lot?.is_planned && lot?.shared_operation_ids?.length > 0 ? true : false;

      //   if (lot) {
      //     setValue("batch_id_in", {
      //       value: lot?.id,
      //       label: lot?.name,
      //       color: lot?.color,
      //       initial_quantity: lot?.quantity,
      //       quantity: lot?.actual_quantity,
      //       planned_quantity: lot?.planned_quantity,
      //       planned_pomaces: lot?.planned_pomaces,
      //       shared_operation_ids: lot?.shared_operation_ids,
      //       composition: lot?.composition,
      //       is_planned: isPlannedByOtherOps,
      //       arrow_quantity: getTotalQuantity(true),
      //     });
      //   }

      //   setValue("priority", {
      //     label: getLabel(`operationPriority${operation?.priority}`),
      //     value: operation?.priority,
      //   });

      //   setDestTanks();

      //   if (operation?.type === "DESTEMMING") {
      //     const tmpPomaces = operation?.pomaces?.[0] || {};
      //     const pomaces = {
      //       quantity: tmpPomaces?.quantity,
      //       cost: tmpPomaces?.cost,
      //       close_notes: tmpPomaces?.notes || "",
      //     };
      //     setValue("pomaces", pomaces);
      //     setValue("pomaces.dest", {
      //       value: tmpPomaces?.id,
      //       label: tmpPomaces?.recipient,
      //       is_field: false,
      //     });
      //     setValue("pomaces.reason", {
      //       value: tmpPomaces?.waste_type,
      //       label: getLabel(`pomaceReason_${tmpPomaces?.waste_type}`),
      //     });

      //     if (lot?.color === "WHITE" || (lot?.color === "RED" && ["ROSE_MACERATION", "CARBONIC_MACERATION"].includes(subtype?.value))) {
      //       initializeProtocolId(type, protocols, operation?.protocol_id, setValue);
      //       initializeDrugBatches(operation, drugs, setValue);
      //       initializeWhitePomaces(operation);
      //     }
      //   }
      // }
      if (!id && !onConfigSave) {
        const savedInputLot = localStorage.getItem("selectedInputLotOperation");
        if (savedInputLot) {
          const { type, id } = JSON.parse(savedInputLot);
          const lot = lots?.find((l) => l?.id === Number(id));

          if (type === "lot" && id && lot) {
            setValue("batch_id_in", {
              value: lot?.id,
              label: lot?.name,
              color: lot?.color,
              initial_quantity: lot?.quantity,
              quantity: lot?.actual_quantity,
              planned_quantity: lot?.planned_quantity,
              shared_operation_ids: lot?.shared_operation_ids,
              composition: lot?.composition,
              is_planned: lot?.is_planned,
              arrow_quantity: getTotalQuantity(true),
            });

            localStorage.removeItem("selectedInputLotOperation");
          }
        }
      }
    }, [operations, operation, lots, id, onConfigSave]);

    const initializeWhitePomaces = (operation) => {
      const tmpPomacesW = operation?.pomaces?.[1] || {};
      const pomacesW = {
        quantity: tmpPomacesW?.quantity,
        cost: tmpPomacesW?.cost,
        close_notes: tmpPomacesW?.notes || "",
      };
      setValue("pomacesW", pomacesW);
      setValue("pomacesW.dest", {
        value: isWizardMode ? 0 : tmpPomacesW?.id,
        label: tmpPomacesW?.recipient,
        is_field: tmpPomacesW?.is_field === true,
      });
      setValue("pomacesW.reason", {
        value: tmpPomacesW?.waste_type,
        label: getLabel(`pomaceReason_${tmpPomacesW?.waste_type}`),
      });
    };

    const customIdMissingDests = "custom-id-missing-dests";
    const customIdNewOp = "custom-id-new-op";
    const customIdUpdateOp = "custom-id-update-op";
    const sendData = async () => {
      if (Object.keys(errors)?.length > 0) {
        console.error("Form validation errors:", errors);
        return;
      }
      const data = getValues();

      // if (!data?.batch_name_selector?.value) {
      //   data.new_batch_name = data?.batch_name_selector?.label;
      // }
      // return;

      const dests = [];
      const sorted_dest_tanks = data.dest_tanks
        ?.sort(function (a, b) {
          return b?.batch_name_selector?.value - a?.batch_name_selector?.value;
        })
        ?.filter(
          (d) => d?.tankData?.value && d?.quantity,
          // &&
          // ((d?.batch_name_selector === false &&
          //   d?.new_batch_name === "" &&
          //   d?.tankData?.is_planned &&
          //   Number(d?.tankData?.planned_quantity) > 0) || // when extending a planned batch
          //   (d?.batch_name_selector?.value === true && d?.new_batch_name) ||
          //   (d?.batch_name_selector?.value === false &&
          //     d?.batch_name_selector?.label))
        );

      // console.log("=== DestemmingForm.sendData() ===");
      // console.log("data.dest_tanks from getValues():", data.dest_tanks);
      // console.log("dest_tanks from useWatch():", dest_tanks);
      // console.log("sorted_dest_tanks after filter:", sorted_dest_tanks);

      for (const destT of sorted_dest_tanks) {
        const tankData = destT?.tankData;
        // console.log("Processing destT:", destT, "tankData:", tankData);

        // const isPlanned =
        //   tankData?.value &&
        //   tankData?.is_planned_by_other_ops &&
        //   Number(tankData?.quantity) === 0 &&
        //   // Number(tankData?.planned_quantity) === 0 &&
        //   tankData?.is_planned;

        const isPlanned = tankData?.is_planned;

        // const new_batch_name2 = isPlanned
        //   ? tankData?.planned_batch_name
        //   : destT?.new_batch_name;

        const batch_name_label = destT?.batch_name_selector?.label;
        const batch_name_value = destT?.batch_name_selector?.value;
        const new_batch_name = batch_name_value
          ? destT?.new_batch_name
          : batch_name_label || tankData?.planned_batch_name;

        const isPlannedByOtherOps =
          tankData?.is_planned &&
          ((id === null && tankData?.programmed_as_dest?.length > 0) ||
            (id !== null &&
              tankData?.programmed_as_dest?.some(
                (p_id) => p_id !== Number(id),
              )));

        const new_name = (
          isPlanned && isPlannedByOtherOps
            ? tankData?.planned_batch_name
            : batch_name_value
              ? new_batch_name
              : batch_name_label
        )?.trim();

        // console.log('destT', destT, 'tankData', tankData, 'isPlanned', isPlanned, 'isPlannedByOtherOps', isPlannedByOtherOps, 'new_name', new_name);

        const plannedLot =
          lots?.find((l) => l?.name === new_name && l?.status === "PLANNED") ||
          null;
        // const isPlannedByOtherOps = plannedLot
        //   ? plannedLot?.shared_operation_ids?.some(
        //       (opId) => Number(opId) !== Number(id)
        //     )
        //   : false;
        // const isPlannedByOtherOps =
        //   tankData?.is_planned && plannedLot?.shared_operation_ids?.length > 0
        //     ? id
        //       ? plannedLot.shared_operation_ids.some((opId) => Number(opId) !== Number(id))
        //       : true
        //     : false;

        // TO DO isPlannedByOtherOps in CutForm is like isPlannedByOtherOps2 (non funziona se id === null ma uso un lotto giá programmato)

        const isPlannedByOtherOps2 =
          id && tankData?.is_planned
            ? plannedLot?.shared_operation_ids?.some(
                (opId) => Number(opId) !== Number(id),
              ) || false
            : tankData?.is_planned && tankData?.planned_batch_name
              ? true
              : false;

        // if (tankData?.value === 89) console.log(' id && tankData?.is_planned', (tankData?.is_planned && tankData?.planned_batch_name) ? true : false)

        const destLot =
          lots?.find((l) => l?.name === new_name && l.state === "MUST") || null;
        const type = destLot?.id && !isPlannedByOtherOps ? "OLD" : "NEW";

        if (type === null) {
          toast.error(getLabel("toast_invalidType"), {
            toastId: customIdInvalidLotName,
            position: toast.POSITION.BOTTOM_RIGHT,
            exclude: true,
            autoClose: 5000,
          });
          return;
        }
        // const newName = tankData?.is_planned ? tankData?.planned_batch_name :
        //   destT?.batch_name_selector?.value === true
        //     ? destT?.new_batch_name
        //     : destT?.batch_name_selector?.label;

        // if (type === "NEW" && !newBatchIds?.includes(newName)) {
        // newBatchIds.push(newName);
        // TO DO check all cases
        // }

        if (String(new_name)?.length === 0) {
          console.error("Invalid new_name");
          return;
        }

        const t = tanks?.find((t) => t.id === tankData?.value) || {};
        const dest = {
          tank_id: tankData?.value,
          quantity: Number(destT.quantity),
          is_new:
            destT?.batch_name_selector?.value === true &&
            destT?.new_batch_name?.length > 0 &&
            lots?.find((l) => l?.name === new_name && l?.state === "MUST")
              ?.id === undefined,
          new_batch_name: new_name,
          new_batch_id: destLot?.id || null,
          // new_batch_type: type,
          old_batch_id: t?.batch_id || "",
          old_quantity: t?.quantity || 0,
          new_quantity:
            (Number(t?.quantity || 0) || 0) + (Number(destT.quantity) || 0),
          ...(batch_id_in?.color === "WHITE" && {
            batch_type: lotTypeMap?.[new_batch_name],
          }),
        };
        dests.push(dest);
      }
      // console.log("dests", dests);
      // return;

      if (dests?.length === 0) {
        toast.error(getLabel("toast_missingDestTanks"), {
          toastId: customIdInvalidLotName,
          position: toast.POSITION.BOTTOM_RIGHT,
          exclude: true,
          autoClose: 5000,
        });
        return;
      }

      const tmpDate = new Date(data?.expire_date)?.setHours(23, 59, 59);
      const info =
        batch_id_in?.color === "WHITE"
          ? {
              analysisType: "tank",
              isGrapeAnalysis: false,
              protocolFrequency: "spot",
            }
          : null;
      const requestPayload = {
        ...(id && { id: id }),
        src_tanks: [
          {
            color: data?.batch_id_in?.color,
            old_quantity: data?.batch_id_in?.quantity,
            name: data?.batch_id_in?.label,
            batch_id: data?.batch_id_in?.value,
            ...(info && { info }),
          },
        ],
        dest_tanks: dests,
        priority: data?.priority.value,
        // pomaces: data?.pomaces,
        // batch_type: data?.batchType?.value,
        // expire_date: addMinutes(
        //   new Date(data?.expire_date)?.setHours(23, 59, 59),
        //   -new Date(data?.expire_date).getTimezoneOffset()
        // ).toISOString(),
        is_exact_date: isExactDate,
        expire_date: new Date(tmpDate)?.toISOString(),
        type: data?.type,
        subtype: data?.subtype?.value,
        ...(data?.subtype?.value === "CARBONIC_MACERATION" &&
          data?.carbonic_output_state?.value && {
            metadata: {
              carbonic_output_state: data?.carbonic_output_state?.value,
            },
          }),
        winery_id: activeWinery?.id,
        cellarman_ids: Array.isArray(data?.cellarman_ids)
          ? data.cellarman_ids.map((cellMan) => cellMan?.value)
          : [],
        // new_batch_name: data?.new_batch_name,
        batch_id_in: data?.batch_id_in?.value,
        note: data?.note,
      };
      if (type === "DESTEMMING") {
        const tmpPomaces = [];
        if (data?.pomaces) {
          tmpPomaces.push(buildPomace(data.pomaces));
        }
        if (data?.pomacesW && data?.subtype?.value === "WITH_PRESSING") {
          tmpPomaces.push(buildPomace(data.pomacesW));
        }

        requestPayload.pomaces = tmpPomaces;

        if (batch_id_in?.color === "WHITE" || data?.subtype?.value) {
          const analysisType = {
            value: "tank",
          };
          const treatments_data = buildTreatmentsDataArray(
            drugs,
            drug_batches,
            data,
            protocols,
            protocol_id,
            "TREATMENT",
            id,
            operation,
            operations,
            analysisType,
          );

          requestPayload.protocol_id = protocol_id?.value || null;
          requestPayload.treatments_data = treatments_data || [];
        }
      }

      console.log("requestPayload", requestPayload);
      // return;

      // Wizard mode: if onConfigSave is provided, call it instead of API
      if (onConfigSave) {
        // Build pomaces and pomacesW separately for wizard mode to preserve structure
        const pomacesList = data?.pomaces
          ? [buildPomace(data.pomaces)].filter((p) => p?.recipient_id !== null)
          : [];
        const pomacesWList =
          data?.pomacesW && data?.subtype?.value === "WITH_PRESSING"
            ? [buildPomace(data.pomacesW)].filter((p) => p?.recipient_id !== null)
            : [];

        onConfigSave({
          dest_tanks: requestPayload.dest_tanks?.map((d) => {
            const tank = tanks?.find((t) => t.id === d?.tank_id);
            return {
              ...d,
              quantity: d?.new_quantity,
              arrow_quantity: d?.new_quantity,
              batch_name: d?.new_batch_name,
              is_sanitized: tank?.is_sanitized || false,
            };
          }),
          pomaces: pomacesList?.map((p) => ({ ...p, type: "pomaces" })),
          pomacesW: pomacesWList?.map((p) => ({ ...p, type: "pomacesW" })),
          treatments_data: requestPayload.treatments_data || [],
          protocol_id: requestPayload.protocol_id || null,
          operation_params: { subtype: requestPayload.subtype },
          note: data?.note,
        });
        return;
      }

      let response = null;

      if (id && !isDuplicate) {
        response = await toastAxios(
          () => updateOperation(id, requestPayload, axiosPrivate),
          customIdUpdateOp,
          { name: operation?.type || "" },
          getLabel,
        );
      } else {
        response = await toastAxios(
          () => newOperation(requestPayload, axiosPrivate),
          customIdNewOp,
          { name: operation?.type || "" },
          getLabel,
        );
      }
      console.log(response);

      if (response && response?.success) {
        const updated_op = response?.updated_data || null;
        // dispatch(empty());
        // NEW: Pass affected_entities to updateStore for surgical cache updates
        updateStore(updated_op, response?.affected_entities);
        navClickHandler();
      }
    };

    // NEW: Updated to accept affected_entities for surgical cache updates
    const updateStore = async (payloadOp, affectedEntities) => {
      // const tankIds = [...payload?.src_tanks, ...payload?.dest_tanks]?.map(t => t?.tank_id || null)?.filter(Boolean); // remove nulls
      if (id && !isDuplicate) {
        dispatch(updateOp(payloadOp));
      } else {
        dispatch(add(payloadOp));
      }

      // NEW: Surgical cache update using affected_entities
      if (affectedEntities) {
        const cacheManager = new CacheManager(dispatch, axiosPrivate);
        await cacheManager.processAffectedEntities(affectedEntities);
      }
      // ⚠️ FALLBACK: If backend doesn't return affected_entities yet
      else {
        dispatch(emptyLots());
        dispatch(emptyTanks());
      }

      // if (payload.id) {
      //   dispatch(emptyLots());
      // } else {
      //   dispatch(add(payload));
      // }
    };

    const navClickHandler = () => {
      if (id) {
        navigate(`/programming/destemming/${id}`);
      } else {
        navigate("/programming");
      }
    };

    const handleCloseModal = () => {
      setIsOpen(false);
      setIsModalOpen(false);
      if (isBatchSelectorModal === "BATCH_SELECTOR") {
        modalReset({
          drug_batches_tmp: [],
        });
      }
    };

    const getTotalQuantity = (isOperation = false) => {
      const batch_id_in = getValues("batch_id_in") || {};
      if (
        !id &&
        !isOperation &&
        batch_id_in?.is_planned &&
        Number(batch_id_in?.planned_quantity) <= 0
      )
        return 0; // i.e. if the in batch is all planned in other dests

      return (
        (isOperation ? operation?.dest_tanks : dest_tanks)?.reduce(
          (acc, curr) => {
            return acc + (Number(curr?.quantity) || 0);
          },
          0,
        ) || 0
      );
    };

    const getWineLabelsOptions = () => {
      if (!wineLabels || wineLabels?.length === 0) return [];
      if (
        getValues("label") &&
        getValues("label")?.filter((l) => l?.type !== "label")?.length > 0
      )
        return wineLabels?.labels;
      return [...wineLabels?.wine, ...wineLabels?.labels] || [];
    };

    const customIdInvalidColor = "custom-id-invalid-color";
    const customIdInvalidLotName = "custom-id-invalid-lot-name";
    const customIdInvalidQuantities = "custom-id-invalid-quantities";
    const openFormModal = () => {
      // console.log('errors', errors)
      // trigger();

      const data = getValues();
      const expDate = data.expire_date;
      const tmpDate = new Date(expDate)?.setHours(23, 59, 59);
      if (!expDate || !tmpDate || new Date(tmpDate) < new Date()) {
        setError("expire_date", {
          type: "manual",
          message: getLabel("errorDateInThePast"),
        });
        return;
      }

      if (data.dest_tanks?.filter((t) => t?.tankData?.value)?.length === 0) {
        toast(getLabel("toast_destemmingMissingDests"), {
          toastId: customIdMissingDests,
          type: toast.TYPE.ERROR,
          isLoading: false,
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        return;
      } else {
        toast.dismiss(customIdMissingDests);
      }

      if (
        data.dest_tanks?.filter(
          (t) =>
            (!t?.batch_name_selector?.value &&
              t?.batch_name_selector?.label?.length === 0) ||
            (t?.batch_name_selector?.value === true &&
              t?.new_batch_name?.length === 0),
        )?.length > 0
      ) {
        toast(getLabel("toast_destemmingMissingLotSelectors"), {
          toastId: customIdMissingDests,
          type: toast.TYPE.ERROR,
          isLoading: false,
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        return;
      } else {
        toast.dismiss(customIdMissingDests);
      }

      // const filteredTanks = dest_tanks?.filter((d) => d?.tankData?.value);
      // if (filteredTanks?.length === 1)
      //   setValue(
      //     "dest_tanks.0.quantity",
      //     getTotalQuantity()
      //   );

      const destemmingLiters = getTotalQuantity();
      // return;
      const allocated = getTotalQuantity();
      const allocatedInitialQuantity = id ? getTotalQuantity(id > 0) : 0;

      const lot = getValues("batch_id_in");
      const availableForCurrentOp =
        allocatedInitialQuantity + (Number(lot?.planned_quantity) || 0);

      // The current operation can only remove as much as is available
      const totalKgAvailable = Math.min(availableForCurrentOp, allocated); //Number(lot?.quantity);

      if (destemmingLiters <= totalKgAvailable) {
        toast.dismiss(customIdInvalidQuantities);
        // setIsOpen(true);
      } else {
        toast(
          getLabel("toast_destemmingInvalidQuantities", {
            sum: destemmingLiters || "0",
            total: totalKgAvailable,
          }),
          {
            toastId: customIdInvalidQuantities,
            type: toast.TYPE.ERROR,
            isLoading: false,
            position: toast.POSITION.BOTTOM_RIGHT,
          },
        );

        data.dest_tanks?.forEach((_, index) =>
          setError(`dest_tanks.${index}.quantity`, {
            type: "manual",
            message: getLabel("toast_invalidQuantity"),
          }),
        );
        return;
      }
      data.dest_tanks?.forEach((tank) => {
        if (
          tank?.tankData?.quantity > 0 &&
          tank?.tankData?.color &&
          tank?.tankData?.color !== getValues("batch_id_in")?.color
        ) {
          console.log(
            "tank",
            tank?.tankData?.color,
            'getValues("batch_id_in")',
            getValues("batch_id_in")?.color,
          );
          toast.warn(getLabel("toast_WarningDiffentWineColor"), {
            toastId: customIdInvalidColor,
            position: toast.POSITION.BOTTOM_RIGHT,
            exclude: true,
            autoClose: 5000,
          });
          return false;
        }
      });

      setIsOpen(true);
    };

    const getLotData = (lot) => {
      const pomaces = Number(getValues("pomaces")?.quantity) || 0;
      const pomacesW = Number(getValues("pomacesW")?.quantity) || 0;
      const pomacesQuantity = pomaces + pomacesW;
      const allocatedInitialQuantity = id ? getTotalQuantity(id > 0) : 0;
      // Calculate the quantity available for consumption in the current operation:
      // This is the difference between the real (current) quantity and what remains planned after scheduled operations.

      // Get the total allocated quantity across destination tanks (dynamic value)
      const allocated = getTotalQuantity();
      const availableForCurrentOp =
        allocatedInitialQuantity + (Number(lot?.planned_quantity) || 0);

      // The current operation can only remove as much as is available
      const checkedQuantity = allocated > availableForCurrentOp ? 0 : allocated; // avoid value over the available quantity
      const arrowQuantity = id
        ? checkedQuantity
        : Math.min(availableForCurrentOp, allocated);
      // Build the lotData object
      const lotData = {};
      lotData.name = lot?.name || lot?.label || "";
      lotData.color = lot?.color;
      lotData.initial_quantity = lot?.initial_quantity; // Fixed initial capacity (e.g., 100 Kg)

      // Set arrow_quantity: the amount to be removed by the current operation
      lotData.arrow_quantity = arrowQuantity;

      const isPlannedByOtherOps =
        lot?.is_planned && lot?.shared_operation_ids?.length > 0
          ? id
            ? lot.shared_operation_ids.some(
                (opId) => Number(opId) !== Number(id),
              )
            : true
          : false;

      const plannedPomacesOtherOps = isPlannedByOtherOps
        ? lot?.planned_pomaces
            ?.filter((t) => (id ? t?.operation_id !== Number(id) : true))
            ?.reduce((acc, curr) => acc + (Number(curr?.quantity) || 0), 0) || 0
        : 0;

      lotData.is_planned = isPlannedByOtherOps; //lot?.is_planned;
      // Set the planned quantity for the current operation:
      // This represents how much will be consumed (arrowQuantity) and thus how much is 'planned' for removal.
      // (If no operation is executed, lot.quantity remains unchanged.)
      // In an editing context (when 'id' exists), we update values based on current distribution.
      if (id) {
        if (availableForCurrentOp > 0) {
          // For display when editing:
          // - The new "quantity" (i.e. what is scheduled to remain) is the planned_quantity from the lot.
          // - The amount consumed by the current operation is arrowQuantity.
          lotData.quantity = Number(lot?.planned_quantity) || 0; // What is expected to remain after scheduled ops
          lotData.planned_quantity = arrowQuantity; // Amount to be removed by the current op

          // For additional display details:
          // "real_initial_quantity" shows the total available for consumption (availableForCurrentOp)
          // "estimated_initial_quantity" shows what remains if we subtract the arrowQuantity from the current quantity.
          lotData.real_initial_quantity = !isPlannedByOtherOps
            ? Number(lot?.quantity)
            : availableForCurrentOp - plannedPomacesOtherOps;

          lotData.estimated_initial_quantity = !isPlannedByOtherOps
            ? 0
            : Number(lot?.quantity) - availableForCurrentOp;

          // Final quantity calculations after the current operation:
          // The real final quantity would be the current lot.quantity minus the amount removed
          lotData.real_final_quantity =
            (!isPlannedByOtherOps
              ? Number(lot?.quantity)
              : availableForCurrentOp) -
            arrowQuantity -
            pomacesQuantity -
            plannedPomacesOtherOps;
          lotData.estimated_final_quantity = !isPlannedByOtherOps
            ? 0
            : Number(lot?.quantity) - availableForCurrentOp;
        } else {
          // If nothing is available, all values become zero.
          lotData.quantity = 0;
          lotData.planned_quantity = 0;
          lotData.real_initial_quantity = 0;
          lotData.estimated_initial_quantity = 0;
          lotData.real_final_quantity = 0;
          lotData.estimated_final_quantity = 0;
        }
      } else {
        // For non-editing contexts, simply use the lot values.
        lotData.quantity = Number(lot?.quantity) || 0;
        lotData.planned_quantity = Number(lot?.planned_quantity) || 0;
        lotData.real_initial_quantity = Number(lot?.quantity) || 0;
        lotData.estimated_initial_quantity =
          (Number(lot?.quantity) || 0) - arrowQuantity;
        lotData.real_final_quantity =
          (isPlannedByOtherOps //lot?.is_planned
            ? (Number(lot?.planned_quantity) || 0) - arrowQuantity
            : !id
              ? Number(lot?.quantity) - arrowQuantity
              : Number(lot?.quantity)) -
          pomacesQuantity -
          plannedPomacesOtherOps;
        // lotData.batch_name = lot?.name || lot?.label || "";
        lotData.estimated_final_quantity = Number(lot?.planned_quantity) || 0;
      }

      lotData.composition = lot?.composition;

      return lotData;
    };

    const getTankData = (t) => {
      const tank = t?.tankData;
      const quantity =
        tank?.availability >= Number(t?.quantity) && Number(t?.quantity) >= 0
          ? Number(t?.quantity)
          : 0;
      const new_batch_name =
        t?.batch_name_selector?.value === true && t?.new_batch_name
          ? t.new_batch_name
          : t?.batch_name_selector?.value === false
            ? t?.batch_name_selector?.label
            : "";

      const tankData = {};

      tankData.arrow_quantity = !id ? quantity : Number(t?.quantity);
      tankData.capacity = tank?.capacity;
      tankData.type = tank?.type;
      tankData.barrels_num = tank?.barrels_num;
      tankData.is_src = false;
      tankData.old_quantity = tank?.quantity;
      tankData.old_batch_name = tank?.lot || "";
      tankData.quantity = tank?.quantity + quantity;
      tankData.color = tank?.is_planned
        ? tank?.planned_batch_color
        : tank?.quantity > 0
          ? tank?.color
          : getValues("batch_id_in")?.color;

      tankData.is_planned = tank?.is_planned;
      tankData.is_planned_state = tank?.is_planned_state;
      tankData.planned_quantity = tank?.is_planned
        ? Number(tank?.planned_quantity) - (!id ? Number(tank?.quantity) : 0)
        : 0;
      tankData.planned_batch_name = tank?.planned_batch_name;
      tankData.planned_batch_type = tank?.planned_batch_type;

      if (
        tank?.planned_batch_name &&
        tank?.is_planned_by_other_ops &&
        !t?.batch_name_selector?.value
      ) {
        tankData.batch_name = tank?.planned_batch_name || new_batch_name;
        if (id) {
          tankData.batch_name = new_batch_name;
          // tankData.batch_type = tank?.batch_type;
          tankData.planned_quantity =
            tankData.planned_quantity - tankData?.old_quantity;
        }
      } else {
        tankData.batch_name = new_batch_name;
        tankData.old_batch_name = tank?.lot;
      }

      return tankData;
    };

    const setDestTanks = () => {
      // const tmpIsPressed = [];
      const dests = [];

      const tmpLotTypeMap = {};
      operation?.dest_tanks?.map((s) => {
        const tank = tanks?.find((t) => t.id === s.tank_id);

        // const plannedLot = lots?.find(
        //   // planned only by the current op, so the lot name can be updated
        //   (l) => l?.id === s?.batch_id && l?.status === "PLANNED"
        // );
        // const isPlannedByOtherOps =
        //   tank?.is_planned && plannedLot?.shared_operation_ids?.length > 0
        //     ? id
        //       ? plannedLot.shared_operation_ids.some((opId) => Number(opId) !== Number(id))
        //       : true
        //     : false;
        const isPlannedByOtherOps =
          tank?.is_planned &&
          ((id === null && tank?.programmed_as_dest?.length > 0) ||
            (id !== null &&
              tank?.programmed_as_dest?.some((p_id) => p_id !== Number(id))));

        // if (s?.tank_id == 88) console.log('isPlannedByOtherOps', s, plannedLot)
        // if same batch_name as src, simulate OLD because it inherits the src name
        const is_new =
          !isWizardMode && operation?.src_tanks?.[0]?.name === s?.batch_name
            ? false
            : s?.is_new;

        const d = {
          quantity: Number(s?.arrow_quantity),
          tankData: {
            value: tank?.id,
            name: tank?.name,
            type: tank?.type,
            barrels_num: tank?.barrels_num,
            label: tank?.name,
            is_src: false,
            capacity: Number(tank?.capacity),
            is_planned_by_other_ops: isPlannedByOtherOps,
            planned_quantity: isPlannedByOtherOps
              ? Number(tank?.planned_quantity) - Number(s?.arrow_quantity)
              : 0,
            is_planned: isPlannedByOtherOps,
            is_planned_state: tank?.is_planned_state,
            planned_subtype: tank?.planned_subtype,
            planned_batch_name: isPlannedByOtherOps
              ? tank?.planned_batch_name
              : "",
            planned_batch_color: tank?.planned_batch_color,
            planned_batch_state: tank?.planned_batch_state,
            planned_batch_type: tank?.planned_batch_type,
            quantity: Number(tank?.quantity),
            availability:
              Number(tank?.capacity) -
              Number(tank?.planned_quantity) +
              Number(s?.arrow_quantity),
            color: tank?.color,
            lot:
              lots?.find((lot) => lot?.id === tank?.batch_id)?.name ||
              getLabel("tankState_EMPTY"),
            programmed_as_src: tank?.programmed_as_src,
            programmed_as_dest: tank?.programmed_as_dest,
            is_planned_by_other_ops:
              tank?.is_planned &&
              tank?.programmed_as_dest?.filter(
                (p_id) => id === null || p_id !== Number(id),
              )?.length > 0,
          },
          new_batch_name: s?.batch_name || "",
          batch_name_selector: is_new
            ? {
                value: true,
                label: getLabel("lotNewName"),
              }
            : {
                value: false,
                label: s?.batch_name,
              },
        };
        dests.push(d);

        // 1 = FLOWER, 0 = PRESSED → se è FLOWER, metti false → quindi sarà batch_type: 1
        // Se è PRESSED, metti true → batch_type: 0
        tmpLotTypeMap[tank?.batch_name || tank?.planned_batch_name] =
          s?.batch_type; // true = PRESSED
        // tmpIsPressed.push(s?.batch_type);
      });

      replace(dests);

      setLotTypeMap(tmpLotTypeMap);

      // setIsPressed(tmpIsPressed);
    };

    const resetDestTanks = (value) => {
      const initialBatchId = operation?.src_tanks?.[0]?.batch_id || null;
      const newBatchId = value?.value || null;

      // reset dest_tanks when changing src batch (otherwise max and min in dest_tanks do not works)
      if (
        dest_tanks?.length > 0 &&
        dest_tanks?.[0]?.tankData?.value &&
        (!id ||
          (id && newBatchId && initialBatchId && newBatchId !== initialBatchId))
      ) {
        replace(
          dest_tanks?.map((d) => {
            return {
              ...d,
              quantity: 0,
              new_batch_name: "",
              batch_name_selector: { value: false, label: "" },
            };
          }),
        );
      } else if (
        dest_tanks?.length > 0 &&
        dest_tanks?.[0]?.tankData?.value &&
        id
      ) {
        setDestTanks();
      }
    };

    if (
      (
        lots?.filter(
          (lot) => lot?.state === "GRAPE", //&& lot?.id !== getValues("batch_id_in")?.value
        ) || []
      )?.length === 0
    ) {
      return (
        <div className="protocols_alert">
          <h6 className="protocols_alert_title">
            {getLabel("noGrapeLotsAlert")}
          </h6>
          <CTA onClick={() => navigate("/lots/new")}>
            {getLabel("createLot")}
          </CTA>
        </div>
      );
    }

    const getSrcTanksForRecap = () => {
      const src_tank = getLotData(getValues("batch_id_in"));

      return [src_tank];
    };

    const getDestTanksForRecap = () => {
      const isDestemming = type === "DESTEMMING";
      const batch_id_in = getValues("batch_id_in") || {};
      const input_color = getValues("batch_id_in")?.color;

      const dests = dest_tanks?.map((d, index) => {
        const new_batch_name =
          d?.batch_name_selector?.value === true
            ? d?.new_batch_name
            : d?.batch_name_selector?.label || d?.tankData?.planned_batch_name;
        return {
          ...d?.tankData,
          batch_type: lotTypeMap?.[new_batch_name],
          state:
            batch_id_in?.color === "WHITE" && isDestemming
              ? "MUST"
              : batch_id_in?.state || Number(d?.tankData?.quantity) > 0
                ? d?.tankData?.state
                : null,
          name: d?.tankData?.label,
          color:
            subtype?.value === "ORANGE_WINE" && input_color === "WHITE"
              ? "ORANGE"
              : ["ROSE_MACERATION", "CARBONIC_MACERATION"].includes(
                    subtype?.value,
                  ) && input_color === "RED"
                ? "ROSE"
                : input_color || d?.tankData?.color,
          add_quantity: Number(d?.quantity),
          old_quantity: Number(d?.quantity) + Number(d?.tankData?.quantity),
          planned_quantity:
            d?.tankData?.is_planned && d.tankData?.is_planned_by_other_ops
              ? Number(d?.tankData?.planned_quantity) -
                Number(d?.tankData?.quantity)
              : 0,
          quantity: Number(d?.quantity) + Number(d?.tankData?.quantity),
          batch_name: new_batch_name,
          is_planned:
            d?.tankData?.is_planned && d.tankData?.is_planned_by_other_ops,
          // batch_name2: d?.tankData?.is_planned ? d?.tankData?.planned_batch_name :
          //   d?.batch_name_selector?.value === true
          //     ? d?.new_batch_name
          //     : d?.batch_name_selector?.label,
        };
      });

      if (isDestemming) {
        const pomaces = getValues("pomaces");
        const p = computePomace(pomaces);
        // add pomaces as a dest tank with quantity and name only
        if (p) dests.push(p);

        if (batch_id_in?.color === "WHITE") {
          const pomacesW = getValues("pomacesW");
          const pW = computePomace(pomacesW);
          // add pomaces as a dest tank with quantity and name only
          if (pW) dests.push(pW);
        }
      }
      return dests;
    };

    const computePomace = (pomaces) => {
      const pomaces_name = pomaces?.dest?.label || "";
      const pomaces_quantity = Number(pomaces?.quantity) || 0;
      if (pomaces_quantity <= 0 || !pomaces_name || pomaces_name?.length === 0)
        return null;
      return {
        type: "pomaces",
        waste_type: pomaces?.reason?.value || null,
        name: pomaces_name,
        color: batch_id_in?.color,
        quantity: pomaces_quantity,
        add_quantity: pomaces_quantity,
        state: batch_id_in?.state,
        batch_name: pomaces?.dest,
      };
    };

    const getTreatmentsProtocols = () => {
      return (
        protocols
          ?.filter((p) => p?.type === "TREATMENT")
          ?.map((p) => {
            return {
              ...p,
              value: p?.protocols_id,
              label: p?.protocols_name,
              name: p?.protocols_name,
            };
          }) || []
      );
    };

    return (
      <div className="operation_form_wrapper">
        <form
          ref={formRef}
          onSubmit={handleSubmit((data) => {
            console.log("DestemmingForm: Form submitted with data:", data);
            if (onConfigSave) {
              console.log("DestemmingForm: Calling sendData for wizard mode");
              return sendData();
            } else {
              console.log(
                "DestemmingForm: Calling openFormModal for manual mode",
              );
              return openFormModal();
            }
          })}
          autoComplete="off"
          noValidate
        >
          <SourceSelector
            id={id}
            type={type}
            configs={configs}
            subtype={subtype?.value}
            operation={operation}
            control={control}
            errors={errors}
            tanks={tanks}
            lots={lots}
            tank_id_in={batch_id_in}
            dest_tanks={dest_tanks}
            getTankData={getTankData}
            setValue={setValue}
            getValues={getValues}
            isMobile={isMobile}
            lotNames={lotNames}
            pomaces_quantity={pomaces?.quantity}
            pomaces_w_quantity={pomacesW?.quantity}
            lockedSubtype={lockedSubtype}
            isWizardMode={!!onConfigSave}
          />
          {/* <h5>{getLabel("fromLot")}</h5>
        <div className="operation_form_wrapper_row">
          <Select
            onTableChange={(value) => resetDestTanks(value)}
            control={control}
            defaultValue={operation?.batch_id_in}
            name="batch_id_in"
            label={getLabel("operationDestemmingLot")}
            placeholder={getLabel("operationDestemmingLotPlaceholder")}
            error={errors}
            required={getLabel("inputRequiredError")}
            options={lots
              ?.filter(
                (lot) =>
                  lot?.state === "GRAPE" &&
                  lot?.id !== getValues("batch_id_in")?.value
              )?.sort((a, b) => {
                // last: disabled
                if (a.is_disabled && !b.is_disabled) return 1;
                if (!a.is_disabled && b.is_disabled) return -1;

                // top: fa-calendar icon
                if (a.icon === "fa-calendar" && b.icon !== "fa-calendar") return -1;
                if (a.icon !== "fa-calendar" && b.icon === "fa-calendar") return 1;

                // case insensitive
                return (a.name || "")?.localeCompare(b.name || "", undefined, {
                  numeric: true,
                  sensitivity: "base", // ignore upper/lowercase and accents
                });
              })
              ?.map?.((lot) => {
                return {
                  value: lot.id,
                  label: lot.name,
                  color: lot.color,
                  composition: lot?.composition,
                  initial_quantity: lot?.quantity,
                  quantity: lot?.actual_quantity,
                  planned_quantity: lot?.planned_quantity,
                  shared_operation_ids: lot?.shared_operation_ids,
                  is_planned: lot?.is_planned,
                  is_disabled:
                    !id &&
                    lot?.is_planned &&
                    Number(lot?.planned_quantity) <= 0, // i.e. if the in batch is all planned in other dests
                  icon:
                    (lot?.is_planned && Number(lot?.planned_quantity) >= 0) ||
                    lot?.is_planned_src > 0
                      ? "fa-calendar"
                      : null,
                };
              })}
            tooltip={getLabel("destemmingBatchInTooltip")}
          />
          {getValues("batch_id_in") && (
            <>
              <DestLotDetail
                lot={getLotData(getValues("batch_id_in"))}
                isRecap={false}
                isMobile={isMobile}
                isName={false}
                isAdditional={true}
              />
            </>
          )}
        </div> */}
          <DestinationSelector
            id={id}
            type={type}
            subtype={subtype?.value}
            operation={operation}
            register={register}
            control={control}
            errors={errors}
            clearErrors={clearErrors}
            setError={setError}
            trigger={trigger}
            setValue={setValue}
            getValues={getValues}
            tanks={tanks}
            lots={lots}
            tank_id_in={batch_id_in}
            fields={fields}
            lotNames={lotNames}
            // dest_tanks={dest_tanks}
            append={append}
            update={update}
            remove={remove}
            getTankData={getTankData}
            isMobile={isMobile}
            watchedArray={dest_tanks}
            lotTypeMap={lotTypeMap}
            setLotTypeMap={setLotTypeMap}
            isWizardMode={!!onConfigSave}
          />

          {type === "DESTEMMING" && (
            <PomacesDestination
              id={id}
              type={type}
              operation={operation}
              dest_tanks={dest_tanks}
              tank_id_in={batch_id_in}
              register={register}
              control={control}
              errors={errors}
              getLabel={getLabel}
              getValues={getValues}
              setValue={setValue}
              watch={watch}
              trigger={trigger}
              clearErrors={clearErrors}
              pomacesRecipients={pomacesRecipients}
              setPomacesRecipients={setPomacesRecipients}
            />
          )}
          {type === "DESTEMMING" &&
            batch_id_in?.color === "WHITE" &&
            getValues("subtype")?.value === "WITH_PRESSING" && (
              <PomacesDestination
                id={id}
                type={type}
                operation={operation}
                dest_tanks={dest_tanks}
                tank_id_in={batch_id_in}
                register={register}
                control={control}
                errors={errors}
                getLabel={getLabel}
                getValues={getValues}
                setValue={setValue}
                watch={watch}
                trigger={trigger}
                clearErrors={clearErrors}
                pomacesRecipients={pomacesRecipients}
                setPomacesRecipients={setPomacesRecipients}
                isWhite={true}
              />
            )}

          {type === "DESTEMMING" &&
            (batch_id_in?.color === "WHITE" ||
              (batch_id_in?.color === "RED" &&
                ["ROSE_MACERATION", "CARBONIC_MACERATION"].includes(
                  subtype?.value,
                ))) && (
              <DrugsBatchSelectorTable
                id={id}
                type={type}
                title={"operationDestemmingPressingDrugs"}
                operation={operation}
                protocol_id={protocol_id}
                configs={configs}
                protocols={getTreatmentsProtocols()}
                drugs={drugs}
                units={units}
                reset={reset}
                errors={errors}
                control={control}
                register={register}
                setValue={setValue}
                getValues={getValues}
                totalTreatmentVolume={totalTreatmentVolume}
                isBatchSelectorModal={isBatchSelectorModal}
                drugBatchFieldArray={drugBatchFieldArray}
                setIsBatchSelectorModal={setIsBatchSelectorModal}
                getLabel={getLabel}
              />
            )}

          {!onConfigSave && (
            <>
              <h6>{getLabel("operationAdditional")}</h6>
              <div className="operation_form_wrapper_row">
                <MultipleSelect
                  name={`cellarman_ids`}
                  control={control}
                  label={getLabel("operationUser")}
                  placeholder={getLabel("operationUserPlaceholder")}
                  error={errors["cellarman_ids"]}
                  required={getLabel("inputRequiredError")}
                  options={users?.map?.((user) => {
                    return {
                      value: user?.id,
                      label: user?.username,
                    };
                  })}
                  tooltip={getLabel("destemmingCellarmenTooltip")}
                />
                <Select
                  name="priority"
                  control={control}
                  label={getLabel("operationPriority")}
                  placeholder={getLabel("operationPriorityPlaceholder")}
                  error={errors}
                  required={getLabel("inputRequiredError")}
                  options={[
                    {
                      label: getLabel("lotQuality_HIGH"),
                      value: "2",
                    },
                    {
                      label: getLabel("lotQuality_MEDIUM"),
                      value: "1",
                    },
                    {
                      label: getLabel("lotQuality_LOW"),
                      value: "0",
                    },
                  ]}
                  // defaultValue={{
                  //     value: "",
                  //     label: "",
                  //   }}
                />
                <DatePicker
                  name="expire_date"
                  control={control}
                  label={getLabel("operationExpireDateForm")}
                  error={errors["expire_date"]}
                  required={getLabel("inputRequiredError")}
                  dateFormat={configs.shortDateFormat}
                  placeholder={getLabel("expiringDatePlaceholder")}
                />
                {getValues("expire_date") && (
                  <Input
                    name={"expire_date_exact"}
                    register={register}
                    label={getLabel("isExactDate")}
                    error={errors["expire_date_exact"]}
                    // isLabel={false}
                    type={"checkbox"}
                    checked={isExactDate}
                    onChange={() => setIsExactDate((prev) => !prev)}
                    customClassName={"confirm no_margin_top"}
                    tooltip={getLabel("destemmingExactDate")}
                  />
                )}
              </div>
            </>
          )}
          <div className="client_form_wrapper_row">
            <TextArea
              name="note"
              register={register}
              label={getLabel("clientNotes")}
              placeholder={getLabel("clientNotesPlaceholder")}
              error={errors["note"]}
            />
          </div>
          {!onConfigSave && (
            <div className="operation_form_wrapper_row">
              <Submit label={getLabel("submitForm")}></Submit>
            </div>
          )}
        </form>
        {isBatchSelectorModal !== "BATCH_SELECTOR" && (
          <ConfirmationModal
            isOpen={isOpen}
            onConfirm={sendData}
            onClose={handleCloseModal}
            description={getLabel("modalNewDestemmingDescription", {
              name: operation?.id || "",
            })}
            isOperationRecap={true}
            totalStep={0}
            errors={errors}
            getValues={getValues}
          >
            <OperationRecap
              type={"DESTEMMING"}
              control={control}
              register={register}
              getValues={getValues}
              errors={errors}
              lots={lots}
              lotNames={lots?.map((l) => l?.name)}
              dest_tanks={getDestTanksForRecap()}
              src_tanks={getSrcTanksForRecap()}
            />
          </ConfirmationModal>
        )}
      </div>
    );
  },
);

DestemmingForm.displayName = "DestemmingForm";

export default DestemmingForm;
